/**
 * FreshCards — Root App Component
 *
 * Composes: WorkspaceProvider → PeekManagerProvider → MainWorkspace
 * MainWorkspace renders: WorkspaceHeader + WorkspaceSurface + peek panels
 */
import React, { useEffect } from 'react';
import { WorkspaceProvider } from './core/workspace/WorkspaceContext';
import { PeekManagerProvider, usePeek } from './ui/components/common/PeekManager';
import WorkspaceSurface from './ui/components/surfaces/WorkspaceSurface';
import { WorkspaceHeader } from './ui/components/common/WorkspaceHeader';
import { QuickNotePanel } from './ui/components/common/QuickNotePanel';
import { AIChatPanel } from './ui/components/common/AIChatPanel';
import { exposePeekHooks } from './ui/components/common/PeekHooks';
import { ThemeProvider } from './ui/themes';
import { Box, Center, Stack, Loader, Text } from '@mantine/core';
import '@mantine/core/styles.css';

/** Shown while workspace initializes */
const WorkspaceLoading: React.FC = () => (
  <Center h="100vh" w="100vw">
    <Stack align="center" gap="md">
      <Loader size="lg" />
      <Text c="dimmed" size="sm">Loading workspace...</Text>
    </Stack>
  </Center>
);

/** Composes header + surface + conditional peek panels */
const MainWorkspace: React.FC = () => {
  const { isPeekOpen } = usePeek();
  return (
    <Box style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <WorkspaceHeader />
      <Box style={{ flex: 1, overflow: 'hidden' }}><WorkspaceSurface /></Box>
      {isPeekOpen('quick-note') && <QuickNotePanel />}
      {isPeekOpen('ai-chat') && <AIChatPanel />}
    </Box>
  );
};

/** Full provider tree — exposes hooks to window on mount */
const FullApp: React.FC = () => {
  useEffect(() => { exposePeekHooks(); }, []);
  return (
    <ThemeProvider>
      <WorkspaceProvider>
        <PeekManagerProvider initialPeekId={null} initialWidth={320}>
          <MainWorkspace />
        </PeekManagerProvider>
      </WorkspaceProvider>
    </ThemeProvider>
  );
};

const App: React.FC = () => <FullApp />;
export default App;